import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { languages, postMessage } from "../utils/helper";
import Loader from "../assets/images/loader.gif";

const Start = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    (async () => {
      try {
        const api_key = searchParams.get("api_key");
        const workflow_id = searchParams.get("workflow_id");
        const statusRes = await fetch("https://sdk-staging.carenow.healthcare/workflow/check-status", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ api_key, workflow_id }),
        });
        const statusData = await statusRes.json();
        if (!statusData?.statusCode?.toString?.().startsWith("2")) throw new Error(statusData?.message || "Check status failed");

        const workflow = statusData?.workflow;
        if (!Array.isArray(workflow)) throw new Error("Invalid workflow data");

        const facematchModule = workflow.find((w) => w.module === "FACEMATCH");
        const presignedUrl = facematchModule?.presigned_urls?.find((p) => p.image_type === "base")?.data ?? null;

        const locale = searchParams.get("locale");
        const autoSubmissionTimeout = parseInt(searchParams.get("auto_submission_timeout"), 10) || 60;
        const baseImageUploaded = searchParams.get("base_image_uploaded") === "true";
        const response = {
          workflow_id,
          user_id: searchParams.get("user_id"),
          facematch_token: searchParams.get("facematch_token"),
          pivc_token: searchParams.get("pivc_token"),
          scan_token: searchParams.get("scan_token"),
        };

        const map = Object.fromEntries(workflow.map((w) => [w.module, w.business_status]));
        const moduleState = {
          shouldDoFaceMatch: map.FACEMATCH !== "COMPLETE",
          shouldDoPIVC: map.PIVC !== "COMPLETE",
          shouldDoVitals: map.VITALS !== "COMPLETE",
        };

        if (!locale || !languages.some((l) => l.locale === locale))
          navigate("/locale-selection", {
            state: { api_key, result: response, moduleState, autoSubmissionTimeout, presignedUrl, baseImageUploaded },
            replace: true,
          });
        else if (moduleState.shouldDoFaceMatch && !baseImageUploaded) {
          if (!presignedUrl) throw new Error("Presigned URL for face match image not found in workflow.");
          navigate("/upload-documents", {
            state: { api_key, result: response, moduleState, locale, presignedUrl, autoSubmissionTimeout },
            replace: true,
          });
        } else if (moduleState.shouldDoVitals)
          navigate("/scan-instructions", {
            state: { api_key, result: response, moduleState, flowType: "FULL_SCAN", locale, autoSubmissionTimeout },
            replace: true,
          });
        else if (moduleState.shouldDoPIVC)
          navigate("/scan-instructions", {
            state: { api_key, result: response, moduleState, flowType: "VIDEO_ONLY", locale, autoSubmissionTimeout },
            replace: true,
          });
        else if (!moduleState.shouldDoFaceMatch && !moduleState.shouldDoPIVC && !moduleState.shouldDoVitals)
          navigate("/scan-result", { state: { api_key, result: response }, replace: true });
        else throw new Error("Unable to determine next step in workflow based on current status.");
      } catch (error) {
        console.error("Error checking workflow status:", error);
        postMessage("error", error?.message || "Failed to initialize workflow");
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, searchParams]);

  return (
    <div className="h-screen flex justify-center items-center bg-primary text-white text-xl text-center">
      <img className="w-3/5" src={Loader} alt="Loading..." />
    </div>
  );
};

export default Start;
